import React, {useMemo, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import JSDB from '../../../fromKotlin/nk';
import {StringToIsrael} from '../../../fromKotlin/nkutils';
import {
    createOrderDataEasy,
    OrderData,
    orderRowVersion,
    writeOrdersToOD,
} from '../../../types/OrderData';
import {deserializeDocument} from '../../../types/DocumentsTypes/utils';
import {getOrderNoteFieldLabel} from '../datasheet/orderNoteFields';

// ---------------------------------------------------------------- palette
const MINE_BG = '#e3f2fd';
const MINE_FG = '#0d47a1';
const SERVER_BG = '#ffe0b2';
const SERVER_FG = '#e65100';
const BOTH_ROW_BG = '#fff8e1';
const HEADER_BG = '#f4f7fc';

const qtyEq = (a: number, b: number) => Math.abs(a - b) < 0.0001;

const fmt = (n: number) => {
    if (!Number.isFinite(n) || n === 0) return '0';
    return String(Math.round(n * 1000) / 1000);
};

const asNum = (v: any): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const asStr = (v: any): string => (v == null ? '' : String(v)).trim();

const productName = (id: number, server?: any): string => {
    try {
        const p = JSDB().getClientProduct(id)?.first;
        const n = p?.name ?? p?.getName?.();
        if (n) return String(n);
    } catch {
        /* ignore */
    }
    const fromServer = (server?.products || []).find((p: any) => Number(p.id) === id);
    return fromServer?.name || `#${id}`;
};

const unitLabel = (id: number, unitIdx: number): string => {
    try {
        const p = JSDB().getClientProduct(id)?.first;
        const units = p?.getOrderProduct?.()?.getUnits?.() ?? p?.available_units;
        if (Array.isArray(units) && units[unitIdx]) return String(units[unitIdx]);
    } catch {
        /* ignore */
    }
    return unitIdx === 0 ? 'יח׳' : `יח׳${unitIdx + 1}`;
};

const collectIds = (...ds: (OrderData | null | undefined)[]): number[] => {
    const ids = new Set<number>();
    ds.forEach((d) => {
        Object.keys(d || {}).forEach((k) => {
            if (k.startsWith('p_')) {
                const id = Number(k.slice(2));
                if (id > 0) ids.add(id);
            }
        });
    });
    return Array.from(ids).sort((a, b) => a - b);
};

const formatWhen = (raw: any): string => {
    if (raw == null || raw === '') return 'לא ידוע';
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
        return raw.toLocaleString('he-IL');
    }
    const s = String(raw);
    try {
        return StringToIsrael(s, true);
    } catch {
        return s;
    }
};

const currentUserName = (): string => {
    try {
        const u = JSDB().getUser()?.first;
        return String(u?.user_name || u?.name || u?.user_company_print_name || 'אני');
    } catch {
        return 'אני';
    }
};

/**
 * Server document (ProductNote) → OrderData shaped like `like`, so fields compare 1:1.
 * Order-owned fields that `writeOrdersToOD` only fills when the document has them are cleared
 * first. Otherwise a missing server value would keep the local row's value and hide the conflict.
 */
const toOrderData = (like: OrderData, doc: any): OrderData | null => {
    if (!like || !doc) return null;
    try {
        const next = createOrderDataEasy(like);
        next.delivery_date = undefined;
        next.forced_business_name = undefined;
        next.forced_address = undefined;
        next.orderDriver = undefined;
        next.force_position_on = false;
        if (like.force_position_on && like.daily_position != null) {
            next.driver_position = like.daily_position;
        }
        writeOrdersToOD(next, doc);
        return next;
    } catch (e) {
        console.log(e);
        return null;
    }
};

/**
 * The copy this row was loaded from (pre-edit). Grid rows keep the loaded `OrderNote` in
 * `nkObject`, so we can rebuild it; when unavailable the diff is two-way (mine vs server).
 */
const baseFromMine = (mine: OrderData | null | undefined): OrderData | null => {
    const nk = mine?.nkObject;
    if (!mine || !nk) return null;
    try {
        const doc = deserializeDocument(nk);
        return doc ? toOrderData(mine, doc) : null;
    } catch {
        return null;
    }
};

// ---------------------------------------------------------------- diff model

export type ConflictDiff = {
    key: string;
    label: string;
    mine: string;
    server: string;
    /** My value differs from the copy I loaded (I touched this). Undefined = base unknown. */
    mineChanged?: boolean;
    /** Server value differs from the copy I loaded (they touched this). Undefined = base unknown. */
    serverChanged?: boolean;
    /** Set for product lines. */
    productId?: number;
};

export const diffIsContested = (d: ConflictDiff) => !!d.mineChanged && !!d.serverChanged;

export const diffTag = (d: ConflictDiff): string => {
    if (d.mineChanged == null || d.serverChanged == null) return '';
    if (d.mineChanged && d.serverChanged) return 'שניכם';
    if (d.mineChanged) return 'שינית';
    if (d.serverChanged) return 'שונה בשרת';
    return '';
};

/** Every order field NEW_ALL_CLIENT_ORDER persists, besides product lines and collection boxes. */
const ORDER_FIELDS: [keyof OrderData, string][] = [
    ['status', 'סטטוס'],
    ['date', 'תאריך'],
    ['delivery_date', 'תאריך אספקה'],
    ['notes', 'הערות'],
    ['notes2', 'הערות 2'],
    ['notes3', 'הערות 3'],
    ['notes4', 'הערות 4'],
    ['notes5', 'הערות 5'],
    ['notes6', 'הערות 6'],
    ['notes7', 'הערות 7'],
    ['notes8', 'הערות 8'],
    ['notes9', 'הערות 9'],
    ['notes10', 'הערות 10'],
    ['category', 'קטגוריה'],
    ['orderDriver', 'נהג זמני'],
    ['forced_business_name', 'שם עסק (כפוי)'],
    ['forced_address', 'כתובת (כפויה)'],
    ['ref_id', 'מזהה שכפול'],
];

const orderFieldLabel = (key: string, fallback: string): string => {
    if (key === 'notes' || /^notes\d+$/.test(key)) return getOrderNoteFieldLabel(key, fallback);
    return fallback;
};

/** Forced route position lives on the order; the daily position does not. */
const forcedPositionValue = (d?: OrderData | null): string => {
    if (!d?.force_position_on) return '';
    const n = Number(d.driver_position);
    if (!Number.isFinite(n) || n === -1) return '';
    return String(Math.round(n));
};

const collectBoxIds = (...ds: (OrderData | null | undefined)[]): string[] => {
    const ids = new Set<string>();
    ds.forEach((d) => {
        Object.keys(d || {}).forEach((k) => {
            if (k.startsWith('c_') && k !== 'c_category') ids.add(k.slice(2));
        });
    });
    return Array.from(ids).sort((a, b) => a.localeCompare(b, 'he'));
};

const collectorLabel = (id: string): string => {
    try {
        const found = JSDB().getCollectors()?.first?.toArray?.().find((c: any) => String(c.id) === id);
        if (found?.user_name) return String(found.user_name);
    } catch {
        /* ignore */
    }
    return `מלקט ${id}`;
};

const usePriceLabel = (v: number): string => {
    if (v === 1) return 'ללא מע״מ';
    if (v === 2) return 'לפני מע״מ';
    if (v === 3) return 'כולל מע״מ';
    return '';
};

/**
 * Only the rows where my copy differs from the server copy. `base` (what I loaded) lets each
 * row say who touched it; without it the flags are left undefined.
 */
export const computeOrderConflictDiff = (
    mine: OrderData | null | undefined,
    server: OrderData | null | undefined,
    base?: OrderData | null,
    serverDoc?: any,
): { fields: ConflictDiff[]; lines: ConflictDiff[] } => {
    if (!mine || !server) return {fields: [], lines: []};
    const fields: ConflictDiff[] = [];
    const pushField = (key: string, label: string, mineVal: string, serverVal: string, baseVal?: string) => {
        if (mineVal === serverVal) return;
        fields.push({
            key,
            label,
            mine: mineVal || '—',
            server: serverVal || '—',
            mineChanged: baseVal == null ? undefined : mineVal !== baseVal,
            serverChanged: baseVal == null ? undefined : serverVal !== baseVal,
        });
    };
    ORDER_FIELDS.forEach(([k, label]) => {
        pushField(String(k), orderFieldLabel(String(k), label), asStr(mine[k]), asStr(server[k]), base ? asStr(base[k]) : undefined);
    });
    pushField(
        'force_position',
        'סדר מאולץ',
        forcedPositionValue(mine),
        forcedPositionValue(server),
        base ? forcedPositionValue(base) : undefined,
    );
    collectBoxIds(mine, server, base).forEach((id) => {
        const m = asNum(mine[`c_${id}`]);
        const s = asNum(server[`c_${id}`]);
        if (qtyEq(m, s)) return;
        const b = base ? asNum(base[`c_${id}`]) : undefined;
        fields.push({
            key: `c_${id}`,
            label: collectorLabel(id),
            mine: m ? fmt(m) : '—',
            server: s ? fmt(s) : '—',
            mineChanged: b == null ? undefined : !qtyEq(m, b),
            serverChanged: b == null ? undefined : !qtyEq(s, b),
        });
    });

    const lineOf = (d: OrderData | null | undefined, id: number) => ({
        qty: asNum(d?.[`p_${id}`]),
        unit: asNum(d?.[`u_${id}`]),
        price: asNum(d?.[`price_${id}`]),
        discount: asNum(d?.[`discount_${id}`]),
        usePrice: asNum(d?.[`use_price_${id}`]),
        lock: asNum(d?.[`lock_${id}`]) ? 1 : 0,
        notes: asStr(d?.[`np_${id}`]),
    });
    const lineEq = (a: ReturnType<typeof lineOf>, b: ReturnType<typeof lineOf>) => {
        if (a.qty === 0 && b.qty === 0) return true;
        return qtyEq(a.qty, b.qty)
            && a.unit === b.unit
            && qtyEq(a.price, b.price)
            && qtyEq(a.discount, b.discount)
            && a.usePrice === b.usePrice
            && a.lock === b.lock
            && a.notes === b.notes;
    };
    const lineLabel = (id: number, l: ReturnType<typeof lineOf>) => {
        if (l.qty === 0) return '—';
        const parts = [`${fmt(l.qty)} ${unitLabel(id, l.unit)}`];
        if (l.price) parts.push(`₪${fmt(l.price)}`);
        if (l.discount) parts.push(`הנחה ${fmt(l.discount)}`);
        const tax = usePriceLabel(l.usePrice);
        if (tax) parts.push(tax);
        if (l.lock) parts.push('נעול');
        if (l.notes) parts.push(l.notes);
        return parts.join(' · ');
    };

    const lines: ConflictDiff[] = [];
    collectIds(mine, server, base).forEach((id) => {
        const m = lineOf(mine, id);
        const s = lineOf(server, id);
        if (lineEq(m, s)) return;
        const b = base ? lineOf(base, id) : null;
        lines.push({
            key: `p_${id}`,
            label: productName(id, serverDoc),
            mine: lineLabel(id, m),
            server: lineLabel(id, s),
            mineChanged: b ? !lineEq(m, b) : undefined,
            serverChanged: b ? !lineEq(s, b) : undefined,
            productId: id,
        });
    });

    const byContested = (a: ConflictDiff, b: ConflictDiff) => Number(diffIsContested(b)) - Number(diffIsContested(a));
    return {fields: fields.sort(byContested), lines: lines.sort(byContested)};
};

/** Who saved the server copy and when (`e_agent` / `action_time` on the order). */
export const conflictServerMeta = (server: any): { agent: string; time: string } => {
    const nk = server?.nkObject;
    const rawAgent = typeof nk?.e_agent === 'function' ? nk.e_agent() : (nk?.e_agent ?? server?.header?.e_agent);
    const rawTime = typeof nk?.action_time === 'function' ? nk.action_time() : (nk?.action_time ?? server?.header?.action_time);
    return {agent: rawAgent ? String(rawAgent) : 'לא ידוע', time: formatWhen(rawTime)};
};

/** All differing rows (fields first, then product lines) between my row and a server document. */
export const conflictDiffs = (mine: OrderData, server: any): ConflictDiff[] => {
    if (!mine || !server) return [];
    const theirs = toOrderData(mine, server);
    if (!theirs) return [];
    const d = computeOrderConflictDiff(mine, theirs, baseFromMine(mine), server);
    return [...d.fields, ...d.lines];
};

/** Number of product lines + fields that differ between my row and the server copy. */
export const conflictDiffCount = (mine: OrderData, server: any): number => conflictDiffs(mine, server).length;

// ---------------------------------------------------------------- UI

type Props = {
    open: boolean;
    /** My copy (the values I tried to save). */
    mine?: OrderData | null;
    /** Server document (ProductNote / ExtendedDocument) saved by the other user. */
    server?: any;
    /**
     * The copy I loaded and edited from. Optional — when omitted it is rebuilt from
     * `mine.nkObject` (grid rows) if possible.
     */
    base?: OrderData | null;
    loading?: boolean;
    applying?: boolean;
    /** Extra line under the title (e.g. "N more rows from this save are waiting"). */
    note?: string;
    onClose: () => void;
    onTakeMine: () => void | Promise<void>;
    onTakeServer: () => void | Promise<void>;
};

const SideCard = ({title, who, when, version, bg, fg}: { title: string; who: string; when: string; version: number; bg: string; fg: string }) => (
    <Box sx={{p: 1.25, bgcolor: bg, borderRadius: 1.5, minWidth: 0}}>
        <Typography variant="caption" sx={{color: fg, fontWeight: 700, display: 'block'}}>{title}</Typography>
        <Typography variant="body2" sx={{fontWeight: 600}} noWrap>{who}</Typography>
        <Typography variant="caption" color="text.secondary" display="block">{when}</Typography>
        <Typography variant="caption" color="text.secondary">גרסה {version}</Typography>
    </Box>
);

const TagCell = ({d}: { d: ConflictDiff }) => {
    const tag = diffTag(d);
    if (!tag) return <TableCell sx={{width: 92}} />;
    const contested = diffIsContested(d);
    return (
        <TableCell sx={{width: 92, py: 0.5}}>
            <Chip
                size="small"
                label={tag}
                sx={{
                    height: 22,
                    fontSize: 11,
                    fontWeight: contested ? 700 : 500,
                    bgcolor: contested ? SERVER_BG : d.mineChanged ? MINE_BG : '#eceff1',
                    color: contested ? SERVER_FG : d.mineChanged ? MINE_FG : 'text.secondary',
                }}
            />
        </TableCell>
    );
};

const DiffRows = ({rows}: { rows: ConflictDiff[] }) => (
    <>
        {rows.map((d) => {
            const contested = diffIsContested(d);
            return (
                <TableRow key={d.key} sx={contested ? {bgcolor: BOTH_ROW_BG} : undefined}>
                    <TableCell sx={{fontWeight: 600}}>{d.label}</TableCell>
                    <TableCell sx={{color: MINE_FG, fontWeight: d.mineChanged ? 700 : 400, whiteSpace: 'pre-wrap'}}>{d.mine}</TableCell>
                    <TableCell sx={{color: SERVER_FG, fontWeight: d.serverChanged ? 700 : 400, whiteSpace: 'pre-wrap'}}>{d.server}</TableCell>
                    <TagCell d={d} />
                </TableRow>
            );
        })}
    </>
);

const SectionHead = ({label, hasTags}: { label: string; hasTags: boolean }) => (
    <TableHead>
        <TableRow sx={{bgcolor: HEADER_BG}}>
            <TableCell sx={{fontWeight: 700, color: 'text.secondary'}}>{label}</TableCell>
            <TableCell sx={{fontWeight: 700, color: MINE_FG}}>שלי</TableCell>
            <TableCell sx={{fontWeight: 700, color: SERVER_FG}}>בשרת</TableCell>
            <TableCell sx={{fontWeight: 700, color: 'text.secondary', width: 92}}>{hasTags ? 'מי שינה' : ''}</TableCell>
        </TableRow>
    </TableHead>
);

const OrderConflictDialog = ({
    open,
    mine,
    server,
    base: baseProp,
    loading,
    applying,
    note,
    onClose,
    onTakeMine,
    onTakeServer,
}: Props) => {
    const [busy, setBusy] = useState<'mine' | 'server' | null>(null);

    const theirs = useMemo(() => toOrderData(mine!, server), [mine, server]);
    const base = useMemo(() => baseProp ?? baseFromMine(mine), [baseProp, mine]);
    const diff = useMemo(() => computeOrderConflictDiff(mine, theirs, base, server), [mine, theirs, base, server]);
    const total = diff.fields.length + diff.lines.length;
    const contested = diff.fields.filter(diffIsContested).length + diff.lines.filter(diffIsContested).length;
    const hasTags = !!base;

    const {agent: serverAgent, time: serverTime} = conflictServerMeta(server);
    const mineTime = mine?.last_updated ? formatWhen(mine.last_updated) : 'עריכה שלא נשמרה';

    const run = async (kind: 'mine' | 'server') => {
        setBusy(kind);
        try {
            if (kind === 'mine') await onTakeMine();
            else await onTakeServer();
        } finally {
            setBusy(null);
        }
    };

    const disabled = !!loading || !!applying || !!busy || !mine;

    return (
        <Dialog open={open} onClose={disabled ? undefined : onClose} maxWidth="md" fullWidth dir="rtl">
            <DialogTitle sx={{pb: 1}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                    <span>
                        ההזמנה השתנתה בינתיים — {mine?.customer || ''}
                        {mine?.orderId ? ` (#${mine.orderId})` : ''}
                    </span>
                    {!loading && theirs ? (
                        <Chip
                            size="small"
                            color={total === 0 ? 'success' : contested > 0 ? 'warning' : 'default'}
                            label={total === 0 ? 'אין הבדלים' : contested > 0 ? `${total} הבדלים · ${contested} בהתנגשות` : `${total} הבדלים`}
                        />
                    ) : null}
                </Box>
            </DialogTitle>
            <DialogContent>
                {note ? (
                    <Typography variant="body2" sx={{mb: 1.5, p: 1, bgcolor: BOTH_ROW_BG, borderRadius: 1}}>
                        {note}
                    </Typography>
                ) : null}
                {loading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
                        <CircularProgress />
                    </Box>
                ) : !theirs ? (
                    <Typography>לא ניתן לטעון את ההזמנה מהשרת. נסה שוב.</Typography>
                ) : (
                    <>
                        <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5}}>
                            <SideCard title="שלי" who={currentUserName()} when={mineTime} version={orderRowVersion(base ?? mine)} bg={MINE_BG} fg={MINE_FG} />
                            <SideCard title="בשרת (השמירה האחרת)" who={serverAgent} when={serverTime} version={orderRowVersion(theirs)} bg={SERVER_BG} fg={SERVER_FG} />
                        </Box>

                        {total === 0 ? (
                            <Box sx={{p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1.5}}>
                                <Typography variant="body2" sx={{color: '#1b5e20', fontWeight: 600}}>
                                    אין הבדל בין השינוי שלך לגרסה בשרת
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    מישהו אחר שמר את אותם ערכים. אפשר ללחוץ "קח את שלי" כדי לשמור שוב, או "קח מהשרת" לסיים.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.75}}>
                                    <Typography variant="caption" color="text.secondary">מוצג רק מה ששונה בין</Typography>
                                    <Chip size="small" label="שלי" sx={{height: 20, fontSize: 11, bgcolor: MINE_BG, color: MINE_FG, fontWeight: 700}} />
                                    <Typography variant="caption" color="text.secondary">ל־</Typography>
                                    <Chip size="small" label="בשרת" sx={{height: 20, fontSize: 11, bgcolor: SERVER_BG, color: SERVER_FG, fontWeight: 700}} />
                                    {hasTags && contested > 0 ? (
                                        <Typography variant="caption" sx={{color: SERVER_FG, ml: 'auto'}}>
                                            שורות מודגשות — שניכם שיניתם אותן
                                        </Typography>
                                    ) : null}
                                </Box>
                                <Box sx={{border: '1px solid #dfe6e9', borderRadius: 1.5, overflow: 'hidden'}}>
                                    <Table size="small">
                                        {diff.fields.length > 0 ? (
                                            <>
                                                <SectionHead label="שדה" hasTags={hasTags} />
                                                <TableBody><DiffRows rows={diff.fields} /></TableBody>
                                            </>
                                        ) : null}
                                        {diff.lines.length > 0 ? (
                                            <>
                                                <SectionHead label="מוצר" hasTags={hasTags} />
                                                <TableBody><DiffRows rows={diff.lines} /></TableBody>
                                            </>
                                        ) : null}
                                    </Table>
                                </Box>
                            </>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1.5}}>
                            קח את שלי — הערכים בעמודה "שלי" ייכתבו על גבי הגרסה בשרת. קח מהשרת — השינוי שלך יבוטל ותוצג הגרסה בשרת.
                        </Typography>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2, gap: 1}}>
                <Button onClick={onClose} disabled={disabled}>סגור</Button>
                <Button
                    variant="contained"
                    color="success"
                    disabled={disabled || !server}
                    onClick={() => run('mine')}
                >
                    {busy === 'mine' ? <CircularProgress size={18} /> : 'קח את שלי (דריסה)'}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={disabled || !server}
                    onClick={() => run('server')}
                >
                    {busy === 'server' ? <CircularProgress size={18} /> : 'קח מהשרת'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrderConflictDialog;
